import React from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import * as Icons from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { DownloadDialog } from "@/components/DownloadDialog";
import { useResourceFiles } from "@/context/ResourceFiles";
import { SLOT_BY_TITLE, formatBytes } from "@/content/resourceCatalog";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
export const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Shield;
  return <Cmp className={className} />;
};

export const Eyebrow = ({ children, className = "", ...rest }) => (
  <p className={`text-[11px] font-bold uppercase tracking-[0.25em] text-[#8E9AEA] ${className}`} {...rest}>
    {children}
  </p>
);

export const AccentTitle = ({ title, as: Tag = "h2", className = "", muted = false }) => (
  <Tag className={`font-display font-bold tracking-tight text-white ${className}`}>
    {title.pre}
    <span className={muted ? "text-[#B7BFDD]" : "text-[#8E9AEA]"}>{title.accent}</span>
  </Tag>
);

const IconTile = ({ name, className = "" }) => (
  <span className={`inline-flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-white/[0.05] ${className}`}>
    <Icon name={name} className="h-5 w-5 text-[#8E9AEA]" />
  </span>
);

export const StatsBand = ({ stats, testId = "stats-band" }) => (
  <section className="border-y border-white/10 bg-[#0D1322]" data-testid={testId}>
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 py-12 sm:gap-10 sm:px-8 sm:py-14 lg:grid-cols-4 lg:px-16">
      {stats.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.07} className="text-center">
          <p className="font-display text-[2rem] font-bold leading-tight tracking-tight text-[#8E9AEA] sm:text-[2.6rem]" data-testid={`stat-value-${i}`}>
            <Counter value={s.value} />
          </p>
          <p className="mt-2 text-[13px] leading-snug text-[#A6B0C9] sm:text-sm">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export const WhyMatters = ({ why }) => (
  <section className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-16 sm:py-24 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-16 lg:py-28" data-testid="why-matters">
    <Reveal>
      <Eyebrow>{why.eyebrow}</Eyebrow>
      <AccentTitle title={why.title} muted className="mt-5 max-w-2xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]" />
      {why.paragraphs.map((p) => (
        <p key={p.slice(0, 24)} className="mt-6 max-w-2xl text-base leading-relaxed text-[#DDE3F2]">
          {p}
        </p>
      ))}
    </Reveal>
    <Reveal delay={0.12}>
      <div className="overflow-hidden rounded-2xl border border-white/10">
        <img
          src={why.image}
          alt={why.imageAlt || "Lanvaus security engineers reviewing detection and compliance evidence"}
          width="1200"
          height="900"
          decoding="async"
          className="hero-photo aspect-[4/3] w-full object-cover"
          loading="lazy"
        />
      </div>
    </Reveal>
  </section>
);

export const BusinessRisk = ({ risk }) => (
  <section className="border-y border-white/10 bg-[#0E1526]" data-testid="business-risk">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:py-24 sm:px-8 lg:grid-cols-[1.3fr_0.8fr] lg:px-16">
      <div>
        <Reveal>
          <Eyebrow>{risk.eyebrow}</Eyebrow>
          <AccentTitle title={risk.title} className="mt-5 max-w-2xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#DDE3F2]">{risk.copy}</p>
        </Reveal>
        <div className="mt-10 flex flex-col gap-4">
          {risk.stats.map((s, i) => (
            <Reveal key={s.value + i} delay={i * 0.07}>
              <div className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-5 transition-colors duration-300 hover:border-[#8E9AEA]/35 sm:gap-6 sm:px-6" data-testid={`risk-stat-${i}`}>
                <span className="w-[4.5rem] shrink-0 font-display text-xl font-bold text-[#8E9AEA] sm:w-24 sm:text-2xl">
                  <Counter value={s.value} />
                </span>
                <p className="text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="glass-card h-full p-6 sm:p-8" data-testid="solution-highlights">
          <h3 className="font-display text-xl font-bold text-white">Solution Highlights</h3>
          <span className="mt-4 block h-0.5 w-12 rounded bg-[#8E9AEA]" />
          <ul className="mt-7 flex flex-col gap-4">
            {risk.highlights.map((h) => (
              <li key={h} className="flex items-start gap-3 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">
                <Icons.CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA]" />
                {h}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Audience = ({ audience, eyebrow = "Who This Is For", testId = "audience" }) => (
  <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid={testId}>
    <Reveal>
      <div className="glass-card grid gap-10 p-6 sm:p-10 lg:grid-cols-[1.1fr_1fr] lg:p-12">
        <div>
          <Eyebrow>{eyebrow}</Eyebrow>
          <AccentTitle title={audience.title} muted className="mt-5 text-2xl sm:text-3xl lg:text-[2.1rem] lg:leading-[1.2]" />
          <p className="mt-5 text-sm leading-relaxed text-[#DDE3F2] sm:text-base">{audience.copy}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {audience.cards.map((c) => (
            <div key={c.title} className="rounded-lg border border-white/10 bg-[#0B0F1A]/70 px-5 py-4" data-testid={`audience-card-${slug(c.title)}`}>
              <p className="text-sm font-semibold text-white">{c.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-[#A6B0C9]">{c.note}</p>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  </section>
);

export const CardGrid = ({ cards, cols = "md:grid-cols-2 lg:grid-cols-3", testPrefix = "card", linkable = false }) => (
  <div className={`grid gap-5 ${cols}`}>
    {cards.map((c, i) => {
      const inner = (
        <>
          <IconTile name={c.icon} />
          <h3 className="mt-6 font-display text-lg font-bold text-white">{c.title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#DDE3F2]/90">{c.copy}</p>
          {linkable && (
            <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white">
              Learn more
              <Icons.ArrowUpRight className="h-4 w-4 text-[#8E9AEA] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          )}
        </>
      );
      return (
        <Reveal key={c.title} delay={i * 0.06}>
          {linkable ? (
            <Link to={c.to} className="glass-card group flex h-full flex-col p-6 sm:p-8" data-testid={`${testPrefix}-${slug(c.title)}`}>
              {inner}
            </Link>
          ) : (
            <div className="glass-card flex h-full flex-col p-6 sm:p-8" data-testid={`${testPrefix}-${slug(c.title)}`}>
              {inner}
            </div>
          )}
        </Reveal>
      );
    })}
  </div>
);

export const Capabilities = ({ capabilities }) => (
  <section id="capabilities" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="core-capabilities">
    <Reveal className="text-center">
      <Eyebrow>Core Capabilities</Eyebrow>
      <AccentTitle title={capabilities.title} className="mx-auto mt-5 max-w-3xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-5xl lg:leading-[1.1]" />
    </Reveal>
    <div className="mt-12 sm:mt-16">
      <CardGrid cards={capabilities.cards} testPrefix="capability" />
    </div>
  </section>
);

const STEP_ICONS = ["ClipboardList", "DraftingCompass", "Rocket", "Activity"];

export const Methodology = ({ eyebrow = "Why Lanvaus", title, copy, steps, showButton = true, testId = "methodology" }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { margin: "-20%" });
  const [active, setActive] = React.useState(0);
  const [locked, setLocked] = React.useState(false);

  React.useEffect(() => {
    if (!inView || locked) return;
    const id = setInterval(() => setActive((a) => (a + 1) % steps.length), 3200);
    return () => clearInterval(id);
  }, [inView, locked, steps.length]);

  return (
    <section className="border-y border-white/10 bg-[#0D1322]" data-testid={testId}>
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16">
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
          <AccentTitle title={title} className="mt-5 max-w-2xl text-[1.75rem] leading-[1.2] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#DDE3F2]">{copy}</p>
          {showButton && (
            <Link to="/about" className="btn-secondary mt-8" data-testid="methodology-about-link">
              Learn About Our Approach
              <Icons.ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </Reveal>

        <div ref={ref} className="mt-12 sm:mt-16">
          <div className="relative mb-8 h-px w-full bg-white/10">
            <motion.span
              className="absolute inset-y-0 left-0 block bg-[#8E9AEA]"
              animate={{ width: `${((active + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => {
              const isActive = i === active;
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div
                    onMouseEnter={() => {
                      setLocked(true);
                      setActive(i);
                    }}
                    onMouseLeave={() => setLocked(false)}
                    onClick={() => {
                      setLocked(true);
                      setActive(i);
                    }}
                    className={`h-full cursor-pointer rounded-xl border px-5 py-6 transition-colors duration-500 sm:px-6 sm:py-7 ${
                      isActive ? "border-[#8E9AEA]/45 bg-[#8E9AEA]/[0.07]" : "border-white/10 bg-transparent"
                    }`}
                    data-testid={`method-step-${slug(s.title)}`}
                  >
                    <div className="flex items-center justify-between">
                      <motion.span
                        animate={{ scale: isActive ? 1.06 : 1 }}
                        transition={{ duration: 0.4 }}
                        className={`inline-flex h-12 w-12 items-center justify-center rounded-lg border transition-colors duration-500 ${
                          isActive ? "border-[#8E9AEA]/50 bg-[#8E9AEA]/15" : "border-white/10 bg-white/[0.05]"
                        }`}
                      >
                        <Icon name={STEP_ICONS[i % 4]} className={`h-5 w-5 ${isActive ? "text-[#8E9AEA]" : "text-[#A6B0C9]"}`} />
                      </motion.span>
                      <span className="font-display text-sm font-bold tracking-[0.3em] text-[#8E9AEA]">0{i + 1}</span>
                    </div>
                    <h3 className="mt-6 font-display text-xl font-bold text-white">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#DDE3F2]/90">{s.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Deliverables = ({ deliverables }) => (
  <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="deliverables">
    <Reveal>
      <Eyebrow>Deliverables</Eyebrow>
      <h2 className="mt-5 font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight lg:text-[2.75rem]">
        What you'll <span className="text-[#8E9AEA]">receive</span>
      </h2>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#DDE3F2]">
        The concrete artifacts you walk away with: reports, configured systems, documentation and runbooks your team can use from day one.
      </p>
    </Reveal>
    <div className="mt-10 sm:mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {deliverables.map((d, i) => (
        <Reveal key={d.title} delay={i * 0.05}>
          <div className="flex h-full items-start gap-5 rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid={`deliverable-${slug(d.title)}`}>
            <IconTile name={d.icon} className="!h-11 !w-11 shrink-0" />
            <div>
              <h3 className="text-base font-semibold text-white">{d.title}</h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">{d.copy}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const TechStack = ({ stack }) => (
  <section className="mx-auto max-w-7xl px-5 pb-16 sm:pb-24 sm:px-8 lg:px-16" data-testid="tech-stack">
    <Reveal>
      <div className="glass-card grid items-center gap-10 p-6 sm:p-10 lg:grid-cols-[1.2fr_1fr] lg:p-12">
        <div>
          <Eyebrow>Technology Stack</Eyebrow>
          <AccentTitle title={stack.title} muted className="mt-5 text-2xl sm:text-3xl lg:text-[2.1rem]" />
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-[#DDE3F2] sm:text-base">{stack.copy}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {stack.badges.map((b) => (
            <span key={b} className="rounded-lg border border-white/10 bg-[#0B0F1A]/70 px-4 py-3.5 text-center text-sm font-medium text-[#DDE3F2]" data-testid={`stack-badge-${slug(b)}`}>
              {b}
            </span>
          ))}
        </div>
      </div>
    </Reveal>
  </section>
);

const BADGE_STYLES = {
  "Free Whitepaper": "border-[#8E9AEA]/40 bg-[#8E9AEA]/10 text-[#8E9AEA]",
  "Free Checklist": "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  Popular: "border-[#8E9AEA]/40 bg-[#8E9AEA]/10 text-[#8E9AEA]",
};

export const ResourceCard = ({ r, delay = 0, service }) => {
  const slot = SLOT_BY_TITLE.get(r.title);
  const { filesBySlot } = useResourceFiles();
  const file = slot ? filesBySlot[slot.slotKey] : null;
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Reveal delay={delay}>
        <div className="glass-card flex flex-col gap-6 p-6 sm:flex-row sm:items-center" data-testid={`resource-${slug(r.title)}`}>
          <IconTile name={r.icon} className={`shrink-0 ${r.badge === "Free Checklist" ? "!border-emerald-400/30 !bg-emerald-400/10 [&>svg]:!text-emerald-300" : ""}`} />
          <div className="flex-1">
            <div className="flex flex-wrap gap-2">
              <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${BADGE_STYLES[r.badge]}`}>{r.badge}</span>
              {r.popular && <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${BADGE_STYLES.Popular}`}>Popular</span>}
              {service && <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#A6B0C9]">{service}</span>}
              {file && (
                <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#A6B0C9]">
                  PDF · {formatBytes(file.size)}
                </span>
              )}
            </div>
            <h3 className="mt-2.5 font-display text-lg font-bold text-white">{r.title}</h3>
            <p className="mt-1.5 text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">{r.copy}</p>
          </div>
          {file ? (
            <button
              onClick={() => setOpen(true)}
              className="btn-primary w-full shrink-0 sm:w-auto sm:!px-5 sm:!py-2.5"
              data-testid={`resource-download-${slug(r.title)}`}
            >
              <Icons.Download className="h-4 w-4" />
              Download
            </button>
          ) : (
            <Link to="/contact" className="btn-secondary w-full shrink-0 sm:w-auto sm:!px-5 sm:!py-2.5" data-testid={`resource-download-${slug(r.title)}`}>
              <Icons.Mail className="h-4 w-4" />
              Request a Copy
            </Link>
          )}
        </div>
      </Reveal>
      {slot && <DownloadDialog slot={{ ...slot, serviceLabel: slot.serviceLabel || service }} file={file} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};

export const CustomResourceCard = ({ item, delay = 0, showPage = false }) => {
  const [open, setOpen] = React.useState(false);
  const slot = {
    slotKey: item.slotKey,
    title: item.title,
    badge: item.badge || "Free Resource",
    serviceLabel: item.pagePath,
  };
  return (
    <>
      <Reveal delay={delay}>
        <div className="glass-card flex flex-col gap-6 p-6 sm:flex-row sm:items-center" data-testid={`custom-resource-${item.slotKey}`}>
          <IconTile name="FileText" className="shrink-0" />
          <div className="flex-1">
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-[#8E9AEA]/40 bg-[#8E9AEA]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8E9AEA]">
                {item.badge || "Free Resource"}
              </span>
              {showPage && (
                <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#A6B0C9]">
                  {item.pagePath}
                </span>
              )}
              <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#A6B0C9]">
                PDF · {formatBytes(item.size)}
              </span>
            </div>
            <h3 className="mt-2.5 font-display text-lg font-bold text-white">{item.title}</h3>
            {item.description && <p className="mt-1.5 text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">{item.description}</p>}
          </div>
          <button onClick={() => setOpen(true)} className="btn-primary w-full shrink-0 sm:w-auto sm:!px-5 sm:!py-2.5" data-testid={`custom-download-${item.slotKey}`}>
            <Icons.Download className="h-4 w-4" />
            Download
          </button>
        </div>
      </Reveal>
      <DownloadDialog slot={slot} file={item} open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export const Resources = ({ resources = [], pagePath }) => {
  const { custom } = useResourceFiles();
  const extra = custom.filter((c) => c.pagePath === pagePath);
  if (resources.length === 0 && extra.length === 0) return null;
  return (
    <section className="mx-auto max-w-7xl px-5 pb-16 sm:pb-24 sm:px-8 lg:px-16" data-testid="related-resources">
      <Reveal>
        <Eyebrow>Resources</Eyebrow>
        <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
          Take something <span className="text-[#8E9AEA]">useful</span> away.
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#DDE3F2] sm:text-base">
          Practical guides written by the engineers who deliver this work. No gated sales deck, no registration wall
          beyond your contact details.
        </p>
      </Reveal>
      <div className="mt-8 flex flex-col gap-4">
        {resources.map((r, i) => (
          <ResourceCard key={r.title} r={r} delay={i * 0.08} />
        ))}
        {extra.map((c, i) => (
          <CustomResourceCard key={c.slotKey} item={c} delay={(resources.length + i) * 0.08} />
        ))}
      </div>
    </section>
  );
};

export const OpenSourceNote = ({ note }) => (
  <section className="mx-auto max-w-7xl px-5 pt-16 sm:px-8 lg:px-16" data-testid="oss-note">
    <Reveal>
      <div className="flex flex-col gap-6 rounded-xl border border-[#8E9AEA]/35 bg-[#8E9AEA]/[0.07] p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-4">
          <IconTile name="PackageCheck" className="shrink-0 !border-[#8E9AEA]/40" />
          <div>
            <p className="font-display text-lg font-bold text-white">{note.heading}</p>
            <p className="mt-1 max-w-2xl text-base leading-relaxed text-[#DDE3F2] sm:text-sm">{note.copy}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 lg:shrink-0">
          <Link to={note.demoTo} className="btn-primary" data-testid="oss-demo-link">
            <Icons.MonitorPlay className="h-4 w-4" />
            {note.demoLabel}
          </Link>
          <Link to={note.managedTo} className="btn-secondary" data-testid="oss-managed-link">
            {note.managedLabel}
            <Icons.ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </Reveal>
  </section>
);

export const EngagementPaths = ({ paths }) => (
  <section className="mx-auto max-w-7xl px-5 py-14 sm:py-20 sm:px-8 lg:px-16" data-testid="engagement-paths">
    <Reveal>
      <Eyebrow data-testid="paths-eyebrow">Two Ways to Engage</Eyebrow>
      <h2 className="mt-5 max-w-2xl font-display text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight">
        Your SOC. <span className="text-[#8E9AEA]">Your call.</span>
      </h2>
    </Reveal>
    <div className="mt-12 grid gap-5 lg:grid-cols-2">
      {paths.map((p, i) => (
        <Reveal key={p.title} delay={i * 0.1}>
          <div className="glass-card flex h-full flex-col p-9" data-testid={`path-card-${slug(p.title)}`}>
            <span className="font-display text-sm font-bold tracking-[0.3em] text-[#8E9AEA]">PATH 0{i + 1}</span>
            <h3 className="mt-4 font-display text-2xl font-bold text-white">{p.title}</h3>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8E9AEA]/80">{p.sub}</p>
            <p className="mt-4 text-sm leading-relaxed text-[#DDE3F2]/90">{p.copy}</p>
            <ul className="mt-6 flex flex-1 flex-col gap-3">
              {p.points.map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-base leading-relaxed text-[#DDE3F2] sm:text-sm">
                  <Icons.CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA]" />
                  {pt}
                </li>
              ))}
            </ul>
            <Link to={p.cta.to} className="btn-secondary mt-8 self-start" data-testid={`path-cta-${slug(p.title)}`}>
              {p.cta.label}
              <Icons.ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);
